<script setup>
// NOTICE: KAPTION-INTEGRATION
// reply-template-picker.TYPES.2 — the media header of a picked template, as a
// compact tile inside the editor. It starts with the template's own example
// (the file Meta approved) when there is one; the agent can replace it with an
// upload (kapt.io, the same service as campaigns) or a pasted link, and name a
// document. v-model is the editor's headerMedia object.
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import KaptionTemplateAttachmentsAPI from 'dashboard/api/kaptionTemplateAttachments';
import { getTemplateExampleMediaUrl } from 'dashboard/components-next/NewConversationModal/helpers/newConversationHelpers';

const props = defineProps({
  template: { type: Object, required: true },
  modelValue: { type: Object, required: true },
  disabled: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

// Meta's limits for template headers (JPEG/PNG 5 MB, MP4 16 MB, PDF), capped
// by the upload service's 15 MB.
const LIMITS = {
  image: {
    accept: '.jpg,.jpeg,.png',
    mimes: ['image/jpeg', 'image/png'],
    mb: 5,
  },
  video: { accept: '.mp4', mimes: ['video/mp4'], mb: 15 },
  document: { accept: '.pdf', mimes: ['application/pdf'], mb: 15 },
};

const fileInput = ref(null);
const isUploading = ref(false);
const error = ref('');
const showUrl = ref(false);
const urlDraft = ref('');
const localPreview = ref('');

const type = computed(() => props.modelValue.media_type || 'image');
const limits = computed(() => LIMITS[type.value] || LIMITS.image);
const exampleUrl = computed(() => getTemplateExampleMediaUrl(props.template));
const url = computed(() => props.modelValue.media_url || '');
const isExample = computed(
  () => props.modelValue.source === 'template_example' && !!url.value
);
const icon = computed(
  () =>
    ({
      image: 'i-lucide-image',
      video: 'i-lucide-film',
      document: 'i-lucide-file-text',
    })[type.value]
);
const typeLabel = computed(() =>
  t(`KAPTION_REPLY_TEMPLATE.MEDIA.TYPE_${type.value.toUpperCase()}`)
);
const title = computed(() => {
  if (isExample.value)
    return t('KAPTION_REPLY_TEMPLATE.MEDIA.FROM_TEMPLATE', {
      type: typeLabel.value,
    });
  if (props.modelValue.file_name) return props.modelValue.file_name;
  if (props.modelValue.media_name) return props.modelValue.media_name;
  if (url.value) {
    try {
      return decodeURIComponent(new URL(url.value).pathname.split('/').pop());
    } catch (e) {
      return url.value;
    }
  }
  return '';
});

const update = patch =>
  emit('update:modelValue', { ...props.modelValue, ...patch });

const useExample = () => {
  localPreview.value = '';
  error.value = '';
  update({
    media_url: exampleUrl.value,
    source: 'template_example',
    attachment_hash: '',
    file_name: '',
  });
};

const clear = () => {
  localPreview.value = '';
  update({ media_url: '', source: '', attachment_hash: '', file_name: '' });
};

const pick = () => {
  if (!props.disabled && !isUploading.value) fileInput.value?.click();
};

const upload = async file => {
  error.value = '';
  if (!limits.value.mimes.includes(file.type)) {
    error.value = t('KAPTION_REPLY_TEMPLATE.MEDIA.WRONG_TYPE', {
      formats: limits.value.accept,
    });
    return;
  }
  if (file.size > limits.value.mb * 1024 * 1024) {
    error.value = t('KAPTION_REPLY_TEMPLATE.MEDIA.TOO_BIG', {
      mb: limits.value.mb,
    });
    return;
  }
  isUploading.value = true;
  if (type.value === 'image') localPreview.value = URL.createObjectURL(file);
  try {
    const { data } = await KaptionTemplateAttachmentsAPI.upload(file);
    update({
      media_url: data.url,
      source: 'upload',
      attachment_hash: data.hash || '',
      file_name: file.name,
      media_name:
        type.value === 'document'
          ? props.modelValue.media_name || file.name
          : props.modelValue.media_name,
    });
  } catch (e) {
    localPreview.value = '';
    error.value =
      e?.response?.data?.error || t('KAPTION_REPLY_TEMPLATE.MEDIA.FAILED');
  } finally {
    isUploading.value = false;
  }
};

const onFile = event => {
  const file = event.target?.files?.[0];
  if (file) upload(file);
  if (fileInput.value) fileInput.value.value = '';
};

const applyUrl = () => {
  const value = urlDraft.value.trim();
  if (!/^https?:\/\//i.test(value)) {
    error.value = t('KAPTION_REPLY_TEMPLATE.MEDIA.BAD_URL');
    return;
  }
  error.value = '';
  localPreview.value = '';
  showUrl.value = false;
  urlDraft.value = '';
  update({
    media_url: value,
    source: 'link',
    attachment_hash: '',
    file_name: '',
  });
};
</script>

<template>
  <div class="flex flex-col gap-1.5" data-testid="picked-template-media">
    <div
      class="flex items-center gap-3 p-2 border rounded-xl border-n-weak bg-n-alpha-black1"
      :class="{ 'border-dashed border-n-ruby-7': !url && !isUploading }"
    >
      <div
        class="flex items-center justify-center overflow-hidden rounded-lg size-12 shrink-0 bg-n-alpha-black2"
      >
        <img
          v-if="type === 'image' && (localPreview || url)"
          :src="localPreview || url"
          alt=""
          class="object-cover size-full"
          data-testid="picked-media-thumb"
        />
        <span v-else :class="icon" class="size-5 text-n-slate-10" />
      </div>

      <div class="flex flex-col min-w-0 flex-1">
        <span
          v-if="isUploading"
          class="text-sm text-n-slate-11"
          data-testid="picked-media-uploading"
        >
          {{ t('KAPTION_REPLY_TEMPLATE.MEDIA.UPLOADING') }}
        </span>
        <template v-else-if="url">
          <span
            class="text-sm font-medium truncate text-n-slate-12"
            data-testid="picked-media-title"
          >
            {{ title }}
          </span>
          <span class="text-xs text-n-slate-10">
            {{
              isExample
                ? t('KAPTION_REPLY_TEMPLATE.MEDIA.EXAMPLE_HINT')
                : typeLabel
            }}
          </span>
        </template>
        <span v-else class="text-sm text-n-ruby-11">
          {{ t('KAPTION_REPLY_TEMPLATE.MEDIA.REQUIRED', { type: typeLabel }) }}
        </span>
      </div>

      <div class="flex items-center gap-1 shrink-0">
        <button
          type="button"
          class="px-2 py-1 text-xs font-medium rounded-lg text-n-brand hover:bg-n-alpha-black2 disabled:opacity-50"
          :disabled="disabled || isUploading"
          data-testid="picked-media-upload"
          @click="pick"
        >
          {{
            url
              ? t('KAPTION_REPLY_TEMPLATE.MEDIA.REPLACE')
              : t('KAPTION_REPLY_TEMPLATE.MEDIA.UPLOAD')
          }}
        </button>
        <button
          type="button"
          class="px-2 py-1 text-xs rounded-lg text-n-slate-11 hover:bg-n-alpha-black2 disabled:opacity-50"
          :disabled="disabled || isUploading"
          data-testid="picked-media-link"
          @click="showUrl = !showUrl"
        >
          {{ t('KAPTION_REPLY_TEMPLATE.MEDIA.LINK') }}
        </button>
        <button
          v-if="exampleUrl && !isExample"
          type="button"
          class="px-2 py-1 text-xs rounded-lg text-n-slate-11 hover:bg-n-alpha-black2"
          :disabled="disabled || isUploading"
          data-testid="picked-media-use-example"
          @click="useExample"
        >
          {{ t('KAPTION_REPLY_TEMPLATE.MEDIA.USE_EXAMPLE') }}
        </button>
        <button
          v-else-if="url && !exampleUrl"
          type="button"
          class="flex items-center justify-center rounded-lg size-7 text-n-slate-10 hover:bg-n-alpha-black2"
          :aria-label="t('KAPTION_REPLY_TEMPLATE.MEDIA.REMOVE')"
          :disabled="disabled || isUploading"
          @click="clear"
        >
          <span class="i-lucide-x size-4" />
        </button>
      </div>
    </div>

    <div v-if="showUrl" class="flex gap-2">
      <input
        v-model="urlDraft"
        type="url"
        :placeholder="t('KAPTION_REPLY_TEMPLATE.MEDIA.URL_PLACEHOLDER')"
        data-testid="picked-media-url"
        class="reset-base flex-1 px-3 py-1.5 text-sm border rounded-lg outline-none bg-n-alpha-black1 border-n-weak text-n-slate-12 focus:border-n-brand"
        @keydown.enter.prevent="applyUrl"
      />
      <button
        type="button"
        class="px-3 py-1.5 text-xs font-medium rounded-lg bg-n-brand text-white"
        data-testid="picked-media-url-apply"
        @click="applyUrl"
      >
        {{ t('KAPTION_REPLY_TEMPLATE.MEDIA.APPLY') }}
      </button>
    </div>

    <input
      v-if="type === 'document'"
      :value="modelValue.media_name"
      type="text"
      maxlength="240"
      :placeholder="t('KAPTION_REPLY_TEMPLATE.MEDIA.DOC_NAME')"
      data-testid="picked-media-doc-name"
      class="reset-base w-full px-3 py-1.5 text-sm border rounded-lg outline-none bg-n-alpha-black1 border-n-weak text-n-slate-12 focus:border-n-brand"
      :disabled="disabled"
      @input="update({ media_name: $event.target.value })"
    />

    <p
      v-if="error"
      class="text-xs text-n-ruby-11"
      data-testid="picked-media-error"
    >
      {{ error }}
    </p>

    <input
      ref="fileInput"
      type="file"
      class="hidden"
      :accept="limits.accept"
      @change="onFile"
    />
  </div>
</template>
